import { Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/shared/lib/utils';

/**
 * Search box for table toolbars.
 *
 * Fully controlled, with no internal state and no effects.
 *
 * An earlier version kept a local `draft`, debounced it, and synced the prop
 * back in through an effect. That is the classic "mirror a prop into state"
 * trap: it needs an effect to stay in sync, the effect fights the parent, and
 * it renders stale values after a back-navigation.
 *
 * The debounce belongs on the *query*, not the input. The URL updates on every
 * keystroke (cheap — it uses `replace`, so no history spam) and the page feeds
 * a debounced copy of that value into its query params. Typing stays instant,
 * requests stay throttled, and there is exactly one owner of the value.
 * See `BrandsPage` for the pairing with `useDebouncedValue`.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn('relative', className)}>
      <Search
        className="text-muted-foreground pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2"
        aria-hidden
      />
      <Input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-8 ps-8 pe-8 text-xs [&::-webkit-search-cancel-button]:appearance-none"
      />
      {value && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onChange('')}
          className="absolute end-1 top-1/2 size-6 -translate-y-1/2"
          aria-label="Clear search"
        >
          <X className="size-3" aria-hidden />
        </Button>
      )}
    </div>
  );
}
